import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Search } from 'lucide-react';

import Navbaar from '../../../components/Navbar/Navbaar';
import CLSidebar from '../../../components/CompanyListPage/CLSidebar/CLSidebar';
import CLCard from '../../../components/CompanyListPage/CLContent/CLCard';
import CLRightPanel from '../../../components/CompanyListPage/CLContent/CLRightPanel';
import CLCompanyProfile from '../../../components/CompanyListPage/CLContent/CLCompanyProfile';

import { fetchCompanies } from '../../../redux/CompanySlice/companySlice';

const CompanyList = () => {
    const dispatch = useDispatch();
    const { companies: reduxCompanies, loading: reduxLoading, error } = useSelector((state) => state.companies);

    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const companiesPerPage = 10;

    useEffect(() => {
        if (!reduxCompanies.length) {
            dispatch(fetchCompanies());
        } else {
            setCompanies(reduxCompanies);
        }
    }, [dispatch, reduxCompanies]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    // Filter companies by search query (name or location)
    const filteredCompanies = useMemo(() => {
        if (!searchQuery.trim()) return companies;
        const q = searchQuery.toLowerCase();
        return companies.filter(c =>
            (c.name || c.companyName || '').toLowerCase().includes(q) ||
            (c.hqLocation || '').toLowerCase().includes(q)
        );
    }, [companies, searchQuery]);

    const indexOfLastCompany = currentPage * companiesPerPage;
    const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
    const currentCompanies = filteredCompanies.slice(indexOfFirstCompany, indexOfLastCompany);
    const totalPages = Math.max(1, Math.ceil(filteredCompanies.length / companiesPerPage));

    const handleCardClick = async (company) => {
        setLoading(true);
        try {
            setSelectedCompany({
                _id: company._id,
                name: company.name || 'Unknown',
                companyLogo: company.companyLogo || 'https://static.thenounproject.com/png/4149271-200.png',
                hqLocation: company.hqLocation || 'Unknown',
                email: company.email || 'N/A',
                description: company.description || 'No description available',
            });
        } catch (err) {
            console.error('Error in handleCardClick:', err.message);
        }
        setLoading(false);
    };

    return (
        <div className="relative h-screen pt-16 flex flex-col">
            <div className='flex w-full'>
                <Navbaar />
            </div>
            <div className="flex flex-row h-full transition-all duration-300">
                {/* Sidebar */}
                <div className="hidden sm:flex sm:flex-col overflow-y-auto bg-[#f5f5f5] border-r w-full sm:w-64 md:w-72 lg:w-80 xl:w-96">
                    <CLSidebar />
                </div>

                {/* Main Content */}
                <div className="flex-1 flex flex-col min-h-0">
                    {/* Page Header with Search */}
                    <div className="px-4 pt-4 pb-3 border-b border-[#ECECEC] bg-white shrink-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <h1 className="font-dmsans font-bold text-xl text-[#333333]">Company Intelligence</h1>
                                <p className="font-dmsans text-xs text-gray-400 mt-0.5">
                                    {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'} — scored across compensation, growth, stability &amp; culture
                                </p>
                            </div>
                            {/* Live Search */}
                            <div className="flex items-center gap-2 bg-[#F7F8FA] border border-[#ECECEC] rounded-lg px-3 py-2 w-full sm:w-64 focus-within:border-[#3F57E0] focus-within:ring-1 focus-within:ring-[#3F57E0] transition-all">
                                <Search className="w-4 h-4 text-gray-400 shrink-0" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Search companies..."
                                    className="bg-transparent outline-none font-dmsans text-sm text-[#333333] placeholder-gray-400 w-full"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Split scrollable area */}
                    <div className="flex-1 flex flex-col px-4 pb-6 min-h-0 mt-4">
                        <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
                            {/* LEFT SCROLLABLE */}
                            <div className="w-full lg:w-[70%] flex flex-col min-h-0 overflow-y-auto hide-scrollbar gap-4">
                                {reduxLoading && companies.length === 0 ? (
                                    <div className="p-4 text-gray-500 font-dmsans">Loading companies...</div>
                                ) : error ? (
                                    <div className="p-4 text-red-500 font-dmsans">Error: {error}</div>
                                ) : filteredCompanies.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                            <Search className="w-5 h-5 text-gray-400" />
                                        </div>
                                        <p className="font-dmsans font-semibold text-[#333333]">No companies found</p>
                                        <p className="font-dmsans text-sm text-gray-400">Try a different search term</p>
                                    </div>
                                ) : (
                                    currentCompanies.map((item, index) => {
                                        const groupedParameterKeys = {
                                            Compensation: Object.keys(item.scores?.compensation?.parameters || {}),
                                            Stability: Object.keys(item.scores?.stability?.parameters || {}),
                                            Growth: Object.keys(item.scores?.careerGrowth?.parameters || {}),
                                            WLB: Object.keys(item.scores?.workLifeBalance?.parameters || {}),
                                            Toxicity: Object.keys(item.scores?.toxicity?.parameters || {}),
                                        };

                                        return (
                                            <div key={item._id || index} onClick={() => handleCardClick(item)} className="cursor-pointer">
                                                <CLCard
                                                    companyLogo={item.companyLogo || 'https://static.thenounproject.com/png/4149271-200.png'}
                                                    companyName={item.name || item.companyName}
                                                    compensation={item.scores.compensation.weightedScore}
                                                    stability={item.scores.stability.weightedScore}
                                                    growth={item.scores.careerGrowth.weightedScore}
                                                    WLB={item.scores.workLifeBalance.weightedScore}
                                                    toxicity={item.scores.toxicity.weightedScore}
                                                    parameterKeys={groupedParameterKeys}
                                                />
                                            </div>
                                        );
                                    })
                                )}

                                {/* Pagination — only shown when there are multiple pages */}
                                {filteredCompanies.length > companiesPerPage && (
                                    <div className="flex items-center justify-center gap-4 pt-4">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                            className={`px-4 py-2 rounded font-dmsans text-sm ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors'}`}
                                        >
                                            Previous
                                        </button>

                                        <span className="text-gray-500 font-dmsans text-sm">
                                            Page {currentPage} of {totalPages}
                                        </span>

                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                            className={`px-4 py-2 rounded font-dmsans text-sm ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors'}`}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* RIGHT SCROLLABLE */}
                            <div className="hidden lg:flex w-full lg:w-[30%] flex-col min-h-0 overflow-y-auto hide-scrollbar bg-white rounded-lg gap-2">
                                {loading ? (
                                    <div className="flex justify-center items-center p-6">
                                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#3F57E0]" />
                                    </div>
                                ) : selectedCompany ? (
                                    <CLCompanyProfile companyData={selectedCompany} />
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-center gap-3 p-6">
                                        <div className="w-10 h-10 rounded-full bg-[#EEF1FF] flex items-center justify-center">
                                            <svg className="w-5 h-5 text-[#3F57E0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                            </svg>
                                        </div>
                                        <p className="font-dmsans font-semibold text-sm text-[#333333]">Select a company</p>
                                        <p className="font-dmsans text-xs text-gray-400 leading-relaxed">
                                            Click any company card to view its full intelligence profile.
                                        </p>
                                    </div>
                                )}
                                <CLRightPanel />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompanyList;
